import {
    Component,
    OnInit,
    HostListener,
    ViewChild,
    ElementRef,
    AfterViewInit,
    OnDestroy,
    ChangeDetectionStrategy
} from "@angular/core";
import {NgForm} from "@angular/forms";
import {lastValueFrom} from "rxjs";
import {
    APCheckSettingsDto,
    APCheckDocumentTypeDto,
    APCheckKeywordDto,
    PerFormaAPIService,
    SchemaDto,
    SchemaKeywordDto
} from "src/app/services/pfapi.service";
import {NzMessageService} from "ng-zorro-antd/message";
import {NzUploadFile} from "ng-zorro-antd/upload";

@Component({
    selector: "app-apcheck-setting",
    templateUrl: "./apcheck-setting.component.html",
    styleUrl: "./apcheck-setting.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ApcheckSettingComponent implements OnInit, AfterViewInit, OnDestroy {
    constructor(
        private readonly api: PerFormaAPIService,
        private message: NzMessageService
    ) {}

    // canvas element=
    @ViewChild("canvasEl") canvasEl!: ElementRef<HTMLCanvasElement>;

    // canvas content
    private context: CanvasRenderingContext2D;

    loading = true;
    APCheckInfo: APCheckSettingsDto;
    originalInfo: APCheckSettingsDto;
    Schema: SchemaDto;
    DocumentTypes: APCheckDocumentTypeDto[];
    AllKeywords: SchemaKeywordDto[];

    testUploadError = "";
    backgroundImage: HTMLImageElement | null = null;
    fileList: NzUploadFile[] = [];

    SelectedDocumentTypeId: number | null;
    DocumentTypeKeywords: APCheckKeywordDto[] = [];
    SelectedBankAccountKeywordId: number | null;
    SelectedCheckNumberKeywordId: number | null;
    SelectedAmountKeywordId: number | null;

    bankTip = "If enabled, check printing will allow you to search by a bank account number.";
    canadaTip = "If enabled, the secondary signature is always applied to checks.";
    dualSigTip = "If the check amount is over this value, the secondary signature will be applied to that check.";
    checkOrderTip = "If disabled, the checks will print in descending order (largest check number first).";
    deleteStampedTip =
        "If disabled, individual checks that are printed during the run will be retained. Leave enabled unless directed by Support.";
    uploadTip =
        "Below you can upload an image of a check. This will allow you to adjust the signature positions and preview where they'll appear when you print. The image is not saved and will be gone the next time you visit this page.";

    dollarFormatter = (value: number): string => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    dollarParser = (value: string): number => Number(value.replace(/[$,\s]/g, ""));

    async ngAfterViewInit() {
        this.context = (this.canvasEl.nativeElement as HTMLCanvasElement).getContext("2d");
    }

    ngOnDestroy(): void {
        this.clearBackgroundImage();
    }

    async ngOnInit() {
        try {
            [this.APCheckInfo, this.Schema] = await Promise.all([
                lastValueFrom(this.api.getApiAPCheckSettings()),
                lastValueFrom(this.api.getApiSystemSchema())
            ]);

            this.originalInfo = JSON.parse(JSON.stringify(this.APCheckInfo));

            this.DocumentTypes = this.Schema.Types.map(x => {
                return {Id: x.Id, Name: x.Name};
            });

            this.AllKeywords = [...this.Schema.Keywords];
            this.SelectedDocumentTypeId = this.APCheckInfo.DocumentType?.Id;

            await this.onDocumentTypeSelected();
        } catch (e) {
            console.error(e);
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                errors.forEach(status => {
                    this.message.error(status);
                });
            } else {
                this.message.error("something went wrong");
            }
        } finally {
            this.loading = false;
            await this.draw();
        }
    }

    onDocumentTypeSelected = async () => {
        if (this.APCheckInfo.DocumentType?.Id != this.SelectedDocumentTypeId) {
            const found = this.Schema.Types.find(x => x.Id == this.SelectedDocumentTypeId);

            this.APCheckInfo.DocumentType = found ? ({Id: found.Id, Name: found.Name} as APCheckDocumentTypeDto) : null;

            this.APCheckInfo.BankAccountKeyword = null;
            this.SelectedBankAccountKeywordId = null;

            this.APCheckInfo.CheckNumberKeyword = null;
            this.SelectedCheckNumberKeywordId = null;

            this.APCheckInfo.AmountKeyword = null;
            this.SelectedAmountKeywordId = null;
        }

        await this.setDocumentTypeKeywords();
    };

    onBankAccountSelected = async () => {
        if (
            this.APCheckInfo.DocumentType &&
            this.APCheckInfo.BankAccountKeyword?.Id != this.SelectedBankAccountKeywordId
        ) {
            const found = this.AllKeywords.find(x => x.Id == this.SelectedBankAccountKeywordId);

            this.APCheckInfo.BankAccountKeyword = found
                ? ({
                      Id: found.Id,
                      DocumentTypeId: this.APCheckInfo.DocumentType.Id,
                      Name: found.Name
                  } as APCheckKeywordDto)
                : null;
        }
    };

    onCheckNumberSelected = async () => {
        if (
            this.APCheckInfo.DocumentType &&
            this.APCheckInfo.CheckNumberKeyword?.Id != this.SelectedCheckNumberKeywordId
        ) {
            const found = this.AllKeywords.find(x => x.Id == this.SelectedCheckNumberKeywordId);

            this.APCheckInfo.CheckNumberKeyword = found
                ? ({
                      Id: found.Id,
                      DocumentTypeId: this.APCheckInfo.DocumentType.Id,
                      Name: found.Name
                  } as APCheckKeywordDto)
                : null;
        }
    };

    onAmountSelected = async () => {
        if (this.APCheckInfo.DocumentType && this.APCheckInfo.AmountKeyword?.Id != this.SelectedAmountKeywordId) {
            const found = this.AllKeywords.find(x => x.Id == this.SelectedAmountKeywordId);

            this.APCheckInfo.AmountKeyword = found
                ? ({
                      Id: found.Id,
                      DocumentTypeId: this.APCheckInfo.DocumentType.Id,
                      Name: found.Name
                  } as APCheckKeywordDto)
                : null;
        }
    };

    setDocumentTypeKeywords = async () => {
        let foundKeywords: APCheckKeywordDto[] = [];

        if (this.SelectedDocumentTypeId) {
            const docType = this.Schema.Types.find(x => x.Id == this.SelectedDocumentTypeId);
            const docTypeKeywords = [...docType.Keywords];

            if (docType) {
                foundKeywords = this.AllKeywords.filter(x => docTypeKeywords.find(t => t.Id == x.Id) != null).map(x => {
                    return {Id: x.Id, DocumentTypeId: docType.Id, Name: x.Name} as APCheckKeywordDto;
                });
            }
        }

        // sort the keywords by name
        this.DocumentTypeKeywords = foundKeywords.sort((a, b) => {
            return a.Name.localeCompare(b.Name);
        });

        this.SelectedBankAccountKeywordId = this.APCheckInfo.BankAccountKeyword?.Id;
        this.SelectedCheckNumberKeywordId = this.APCheckInfo.CheckNumberKeyword?.Id;
        this.SelectedAmountKeywordId = this.APCheckInfo.AmountKeyword?.Id;
    };

    save = async (frm: NgForm) => {
        frm.control.markAllAsTouched();

        if (!frm.valid) {
            this.message.error("All fields are required. Please correct any errors.");
            return;
        }

        this.loading = true;

        try {
            await lastValueFrom(this.api.postApiAPCheckSave(this.APCheckInfo));
            this.updateOriginalData();
            this.message.success("Saved changes");
            frm.control.markAsUntouched();
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                errors.forEach(e => this.message.error(e));
            } else {
                this.message.error("something went wrong");
            }
        } finally {
            this.loading = false;
        }
    };

    //#region Helper Methods

    // check for unsaved changes
    isUnsavedChanges(): boolean {
        if (!this.originalInfo) {
            return false;
        }

        return JSON.stringify(this.APCheckInfo) !== JSON.stringify(this.originalInfo);
    }

    // listen for browser refresh/close
    @HostListener("window:beforeunload", ["$event"])
    beforeUnload(event: BeforeUnloadEvent) {
        if (this.isUnsavedChanges()) {
            event.preventDefault();

            // Legacy/fallback: Set if the property exists (future-proofing)
            if ("returnValue" in event) {
                event.returnValue = true;
            }
        }
    }

    // revert back to the last saved state
    reset = async () => {
        const original = JSON.stringify(this.originalInfo);
        const current = JSON.stringify(this.APCheckInfo);

        if (original === current) {
            this.message.info("There are no unsaved changes.");
            return;
        }

        this.APCheckInfo = JSON.parse(original);

        this.SelectedDocumentTypeId = this.APCheckInfo.DocumentType.Id;
        await this.setDocumentTypeKeywords();
        await this.draw();
    };

    // when various sections are saved, update the original data to mark it "clean"
    updateOriginalData = () => {
        this.originalInfo = JSON.parse(JSON.stringify(this.APCheckInfo));
    };

    /**
     * Capture a file upload event just to grab
     * the image bytes (as a base64 string).
     * @param info
     */
    onBeforeUpload = (file: NzUploadFile): boolean => {
        this.testUploadError = "";

        if (!file.type.startsWith("image/")) {
            this.testUploadError = "Please select an image file";
            return false;
        }

        this.clearBackgroundImage();

        this.getBase64(file)
            .then(base64 => {
                const img = new Image();

                img.onload = () => {
                    this.backgroundImage = img;
                    this.draw();
                };

                img.onerror = e => {
                    console.error("Image failed to load:", e);
                    this.testUploadError = "Failed to display image";
                };

                img.src = base64;
            })
            .catch(err => {
                console.error(err);
                this.testUploadError = "Failed to read image";
            });

        return false;
    };

    clearImage = async () => {
        this.testUploadError = "";
        this.clearBackgroundImage();
        await this.draw();
    };

    /**
     * convert file object to a base64 string
     * @param file
     */
    private getBase64(file): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    }

    private clearBackgroundImage(): void {
        if (this.backgroundImage) {
            this.backgroundImage.src = "";
            this.backgroundImage.onload = null;
            this.backgroundImage.onerror = null;
            this.backgroundImage = null;
        }
    }

    onSigChange = async e => {
        console.log(e);
        await this.draw();
    };

    viewLogs = async () => {
        window.open("/ap/logs", "_blank");
    };

    //#endregion

    //#region Canvas Methods

    async draw() {
        const cw = (this.canvasEl.nativeElement as HTMLCanvasElement).width;
        const ch = (this.canvasEl.nativeElement as HTMLCanvasElement).height;
        const ctx = this.context;

        ctx.clearRect(0, 0, cw, ch);

        const x1 = this.APCheckInfo.PrimarySignatureInfo.X ?? 0;
        const y1 = this.APCheckInfo.PrimarySignatureInfo.Y ?? 0;
        const w1 = this.APCheckInfo.PrimarySignatureInfo.Width ?? 0;
        const h1 = this.APCheckInfo.PrimarySignatureInfo.Height ?? 0;

        const x2 = this.APCheckInfo.SecondarySignatureInfo.X ?? 0;
        const y2 = this.APCheckInfo.SecondarySignatureInfo.Y ?? 0;
        const w2 = this.APCheckInfo.SecondarySignatureInfo.Width ?? 0;
        const h2 = this.APCheckInfo.SecondarySignatureInfo.Height ?? 0;

        // draw background image (if available) stretched to fit canvas
        if (this.backgroundImage) {
            // change the opacity just for the custom image
            const backgroundOpacity = 0.6;
            ctx.globalAlpha = backgroundOpacity;

            ctx.drawImage(
                this.backgroundImage,
                0,
                0,
                this.backgroundImage.width,
                this.backgroundImage.height,
                0,
                0,
                cw,
                ch
            );

            // reset the default opacity
            ctx.globalAlpha = 1.0;
        } else {
            // fallback background color when no image
            ctx.fillStyle = "#fdfdfd";
            ctx.fillRect(0, 0, cw, ch);
        }

        // Paper size in points/pixels @ 72 dpi
        const PAPER_W = 612;
        const PAPER_H = 792;

        const scale = Math.min(cw / PAPER_W, ch / PAPER_H);
        const offsetX = (cw - PAPER_W * scale) / 2;
        const offsetY = (ch - PAPER_H * scale) / 2;

        ctx.save();
        ctx.translate(offsetX, offsetY);
        ctx.scale(scale, scale);

        const actualY1 = PAPER_H - y1 - h1;
        const actualY2 = PAPER_H - y2 - h2;

        // draw some sample text if we
        // aren't using a custom background
        if (!this.backgroundImage) {
            const previewText = "Preview";
            const helpText = "Click to redraw";

            ctx.save();

            const angle = Math.atan2(PAPER_H, PAPER_W);
            ctx.rotate(angle);

            const diagonalLength = Math.sqrt(PAPER_W ** 2 + PAPER_H ** 2);
            const fontSize = diagonalLength / 5;
            ctx.font = `bold ${fontSize.toFixed(1)}px Arial, Helvetica, sans-serif`;
            ctx.fillStyle = "rgba(180, 180, 180, 0.35)";

            ctx.textAlign = "left";
            ctx.textBaseline = "alphabetic";

            const insetX = fontSize * 0.75;
            const insetY = fontSize * 0.3;

            ctx.fillText(previewText, insetX, insetY);

            ctx.restore();

            // write some additional text
            ctx.save();

            const smallFontSize = fontSize * 0.15;

            ctx.font = `normal ${smallFontSize.toFixed(1)}px Arial, Helvetica, sans-serif`;
            ctx.fillStyle = "rgba(140, 140, 140, 0.6)";

            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            const padding = smallFontSize * 1.2;

            ctx.fillText(helpText, padding, PAPER_H * 0.85 - padding);

            ctx.restore();
        }

        ctx.fillStyle = "rgba(0, 0, 255, 0.7)"; // blue with transparency
        ctx.fillRect(x1, actualY1, w1, h1);

        ctx.fillStyle = "rgba(255, 0, 0, 0.7)"; // red with transparency
        ctx.fillRect(x2, actualY2, w2, h2);

        ctx.restore();
    }

    //#endregion
}
